import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useCreateApprovalPolicy } from "@/hooks/useApprovalPolicies";
import { Plus, Save, Trash2, Shield } from "lucide-react";

export function ApprovalPolicyManager({ workspaceId }: { workspaceId: string }) {
  const createPolicy = useCreateApprovalPolicy();
  const [editingPolicy, setEditingPolicy] = useState<any>(null);

  const [policies] = useState([
    {
      id: "1",
      policy_name: "High Value Bills",
      trigger_conditions: { amount_threshold: 10000 },
      approval_levels: [
        { level: 1, approver_role: "manager", required_count: 1 },
        { level: 2, approver_role: "director", required_count: 1 },
      ],
      is_active: true,
    },
  ]);

  const handleSave = async () => {
    if (!editingPolicy) return;
    await createPolicy.mutateAsync({
      ...editingPolicy,
      workspace_id: workspaceId,
    });
    setEditingPolicy(null);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Approval Policy Configuration
        </CardTitle>
        <CardDescription>
          Define multi-level approval workflows based on bill characteristics
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {editingPolicy ? (
          <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
            <div className="space-y-2">
              <Label>Policy Name</Label>
              <Input
                value={editingPolicy.policy_name || ""}
                onChange={(e) =>
                  setEditingPolicy({ ...editingPolicy, policy_name: e.target.value })
                }
                placeholder="e.g., High Value Bills"
              />
            </div>

            <div className="space-y-2">
              <Label>Amount Threshold ($)</Label>
              <Input
                type="number"
                placeholder="10000"
              />
            </div>

            <div className="space-y-2">
              <Label>Approval Levels</Label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input placeholder="Level 1: Manager" disabled />
                  <Button size="sm" variant="outline">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <Button size="sm" variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Level
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={editingPolicy.is_active}
                onCheckedChange={(checked) =>
                  setEditingPolicy({ ...editingPolicy, is_active: checked })
                }
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={createPolicy.isPending}>
                <Save className="h-4 w-4 mr-2" />
                Save Policy
              </Button>
              <Button variant="outline" onClick={() => setEditingPolicy(null)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button
            onClick={() =>
              setEditingPolicy({
                policy_name: "",
                trigger_conditions: {},
                approval_levels: [],
                is_active: true,
              })
            }
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Approval Policy
          </Button>
        )}

        <div className="space-y-2">
          {policies.map((policy) => (
            <Card key={policy.id}>
              <CardContent className="flex items-center justify-between p-4">
                <div>
                  <p className="font-medium">{policy.policy_name}</p>
                  <p className="text-sm text-muted-foreground">
                    {policy.approval_levels.length} approval levels
                  </p>
                </div>
                <div className="flex gap-2 items-center">
                  <Badge variant={policy.is_active ? "outline" : "secondary"}>
                    {policy.is_active ? "Active" : "Inactive"}
                  </Badge>
                  <Button variant="ghost" size="sm" onClick={() => setEditingPolicy(policy)}>
                    Edit
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
